import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import usePageMetadata from '../utils/usePageMetadata';
import './AdSenseGuide.css';

const AdSenseGuide = () => {
  const location = useLocation();
  
  usePageMetadata({
    title: 'Getting Started with Google AdSense - Complete Guide | The Ad Shop',
    description: 'Complete guide to signing up for Google AdSense, getting your website approved, setting up your account, and optimization strategies for maximum revenue.',
    keywords: 'Google AdSense, AdSense guide, monetization, ad optimization, publisher guide, AdSense setup, AdSense approval',
    canonicalUrl: 'https://theadshop.co/guides/adsense',
    meta: [
      { property: 'og:title', content: 'Getting Started with Google AdSense - Complete Guide' },
      { property: 'og:description', content: 'Learn how to set up and optimize Google AdSense for maximum revenue.' },
      { property: 'og:type', content: 'article' },
      { property: 'og:url', content: 'https://theadshop.co/guides/adsense' },
      { name: 'twitter:card', content: 'summary_large_image' },
      { name: 'twitter:title', content: 'Getting Started with Google AdSense - Complete Guide' },
      { name: 'twitter:description', content: 'Complete guide to signing up for Google AdSense and optimizing your ad revenue.' },
    ],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: 'Getting Started with Google AdSense',
      url: 'https://theadshop.co/guides/adsense',
      description: 'Complete guide to signing up for Google AdSense, getting your website approved, setting up your account, and optimization strategies.',
      publisher: {
        '@type': 'Organization',
        name: 'The Media Shop, LLC',
        url: 'https://theadshop.co/',
      },
      datePublished: '2025-01-24',
      dateModified: '2025-01-24',
    },
  });

  // Scroll to anchor on mount if hash is present
  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  }, [location.hash]);

  return (
    <div className="adsense-guide-page">
      <div className="guide-container">
        <header className="guide-header">
          <h1>Getting Started with Google AdSense</h1>
          <p className="guide-subtitle">
            Complete guide to signing up for AdSense, getting your website approved, 
            setting up your account, and optimization strategies.
          </p>
        </header>

        <div className="guide-content-wrapper">
          <aside className="table-of-contents">
            <h2>Table of Contents</h2>
            <nav>
              <a href="#what-is-adsense">What is Google AdSense?</a>
              <a href="#eligibility">Eligibility and Requirements</a>
              <a href="#signing-up">Signing Up for Your AdSense Account</a>
              <a href="#site-rejection">Site Rejection</a>
              <a href="#setting-up">Setting Up Your Approved Site</a>
              <a href="#privacy-consent">Privacy and Messaging</a>
              <a href="#account-info">Account Information</a>
              <a href="#auto-ads">Deploying Ads: Auto Ads</a>
              <a href="#manual-ads">Deploying Ads: Manual Ads</a>
              <a href="#policies">Policies and Compliance</a>
              <a href="#optimization">Optimization</a>
              <a href="#reports">Reports</a>
            </nav>
          </aside>

          <main className="guide-content">
            <section id="what-is-adsense" className="guide-section">
              <h2>What is Google AdSense?</h2>
              <p>
                Google AdSense is a simple and popular advertising program that allows publishers 
                to display ads on their websites and earn revenue when users view or click on those ads. 
                It is user-friendly, doesn't require an ad server, and is suitable for beginners. 
                Most websites begin their monetization journey here, and it's a great tool to get started. 
                We'll cover signing up for AdSense, getting your website approved for ad serving, 
                setting up your account, and optimization strategies.
              </p>
            </section>

            <section id="eligibility" className="guide-section">
              <h2>Eligibility and Requirements</h2>
              <p>
                To begin using AdSense some basic requirements need to be met by the account owners. 
                This process has become more stringent over time with fewer and fewer sites being 
                accepted for the Google AdSense program. The five basic requirements for signing up 
                for an AdSense account are as follows:
              </p>
              <ul>
                <li>
                  <strong>Content Compliance:</strong> Your website must comply with Google's content policies.
                </li>
                <li>
                  <strong>Ownership:</strong> You must own the website or have editing access.
                </li>
                <li>
                  <strong>Age Requirement:</strong> You must be at least 18 years old.
                </li>
                <li>
                  <strong>Traffic Volume:</strong> While there's no minimum traffic requirement, a reasonable 
                  amount of content and visitors can improve approval chances. Generally, you should have 
                  at least 5k visitors a month to your website before applying to AdSense, although in some 
                  instances you can be accepted with less traffic.
                </li>
                <li>
                  <strong>Address Verification:</strong> You'll be able to receive a piece of mail with a 
                  pin code on it from your registered address. This will enable you to verify your address 
                  with Google and begin receiving payments.
                </li>
              </ul>
            </section>

            <section id="signing-up" className="guide-section">
              <h2>Signing Up for Your AdSense Account and Site Approval</h2>
              <p>
                Submitting your site for ad serving with AdSense is a straightforward process that just 
                takes a few minutes. Once the process is completed, your site will be reviewed by Google's 
                automated tools. You'll usually receive a result within 3 days, but there can be exceptions 
                where it takes more time.
              </p>
              <ol>
                <li>
                  <strong>Sign Up for AdSense:</strong> Visit the AdSense website (
                  <a href="https://google.com/adsense" target="_blank" rel="noopener noreferrer">https://google.com/adsense</a>) 
                  and sign up using your Google account.
                </li>
                <li>
                  <strong>Provide Website Details:</strong> Enter your website URL and accept the terms.
                </li>
                <li>
                  <strong>Site Verification:</strong> You'll need to choose a site verification method from 
                  3 available options to verify your ownership or ability to edit the website. Choose one of the following:
                  <ul style={{ marginTop: '0.5rem', marginBottom: '0.5rem' }}>
                    <li>
                      <strong>Ads.txt Posting:</strong> Create a text file called ads.txt and include the line 
                      provided by Google in it. Upload this file to your root website directory. So if your 
                      website was mywebsite.com, you would be able to access the ads.txt file by visiting 
                      mywebsite.com/ads.txt
                    </li>
                    <li>
                      <strong>Code Snippet:</strong> Copy the provided code snippet from Google and paste it 
                      between the &lt;head&gt;&lt;/head&gt; code sections of your website. Be sure the code is 
                      accessible on all pages of your website that you wish to serve ads on.
                    </li>
                    <li>
                      <strong>Meta Tag:</strong> Copy the provided code snippet from Google and paste it between 
                      the &lt;head&gt;&lt;/head&gt; code sections of your website. Be sure the code is accessible 
                      on all pages of your website that you wish to serve ads on.
                    </li>
                  </ul>
                </li>
                <li>
                  <strong>Submit for Review:</strong> Google will review your site for compliance.
                </li>
              </ol>
            </section>

            <section id="site-rejection" className="guide-section">
              <h2>Site Rejection</h2>
              <p>
                If your site is rejected for ad serving there can be many reasons which we will cover below. 
                Keep in mind Google is very vague on the reasons for submission and doesn't provide direct 
                support for resolving the problem. It's extremely annoying, but as we say in the industry, 
                Google will be Google.
              </p>

              <div className="rejection-section">
                <h3>Rejection: Insufficient Content</h3>
                <ul>
                  <li>Low-Quality or Thin Content: Pages with minimal or superficial content that provide little value to users.</li>
                  <li>Under Construction: Sites that are incomplete or have placeholder content.</li>
                  <li>Non-Unique Content: Duplicate or scraped content from other websites without adding original value.</li>
                </ul>
                <div className="solution-box">
                  <strong>Solution:</strong> Google is telling you that your site content isn't up to par. 
                  You need to ensure that you don't have any under-construction pages on your site, that 
                  all of your pages are complete and offer valuable information, and that you aren't just 
                  posting repurposed AI-generated or scraped content. Google has become much more stringent 
                  about this as of late, especially the scraped and AI content. Double-check that if you 
                  went the route of pasting code on your site it's only on pages where you want to show ads.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Site Does Not Comply with Google Policies</h3>
                <ul>
                  <li>Violation of AdSense Program Policies: Non-compliance with guidelines related to content, ad placement, and user experience.</li>
                  <li>Copyright Infringement: Use of content without proper authorization or licensing.</li>
                  <li>User-Generated Content Issues: Failure to moderate user comments or posts that violate policies.</li>
                </ul>
                <div className="solution-box">
                  <strong>Solution:</strong> This is one of the bigger violations that might not be solvable 
                  depending on your content strategy. Google is telling you that your overall content is 
                  against its guidelines. If you're committing copyright infringement, that's not allowed. 
                  Certain other types of content like pornography and gore aren't allowed. If you have a 
                  lot of user-generated content or comments, it could be because you're not moderating these 
                  areas. If this is the case you need to clean up any comments or content that contains 
                  excessive profanity references, links to porn, or any type of discrimination.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Prohibited Content</h3>
                <ul>
                  <li>Adult or Sexual Content: Including pornography, adult themes, or sexually explicit material.</li>
                  <li>Violent Content: Promotion of violence, gore, or physical injury.</li>
                  <li>Illicit Behavior: Content that promotes illegal activities or substances.</li>
                  <li>Hate Speech: Material that incites hatred or discrimination against individuals or groups.</li>
                  <li>Misleading Content: Deceptive practices or content that misleads users.</li>
                </ul>
                <div className="solution-box">
                  <strong>Solution:</strong> This is similar to a Policy Violation but a bit more specific. 
                  Adult and Sexual content needs removed from your site. If you're in the medical space 
                  this can be tricky as their scanning tools can confuse legitimate medical content with 
                  pornography. Excessively violent content can also cause a problem, even if it's actual 
                  news stories. Any illicit behavior promoting illegal activities or drug use needs to be 
                  removed. Hate speech as always is not allowed and news stories can also run into this 
                  problem. Misleading content can refer to posting bad information or UI practices that 
                  trick users into clicking certain things.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Navigation and User Experience Issues</h3>
                <ul>
                  <li>Poor Site Navigation: Broken links, excessive pop-ups, or difficult-to-use menus.</li>
                  <li>Mobile Unfriendly: Lack of mobile optimization affecting usability on smartphones and tablets.</li>
                  <li>Intrusive Ads: Ads that disrupt user experience, such as pop-ups or interstitials that are hard to close.</li>
                </ul>
                <div className="solution-box">
                  <strong>Solution:</strong> This is one of the more straightforward rejections you can get 
                  and is pretty self-explanatory. You need to clean up broken links and hard-to-use menus. 
                  Excessive popups or ones that are hard to close are a big no-no. You should also remove 
                  any other ad providers when applying for AdSense just keep the site clear and ensure you 
                  don't get flagged for bad ads.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Invalid Traffic Concerns</h3>
                <ul>
                  <li>Artificial Traffic Generation: Use of bots, click exchanges, or incentivized traffic to manipulate ad impressions or clicks.</li>
                  <li>Click Fraud: Encouraging users to click on ads or using deceptive layouts to generate accidental clicks.</li>
                </ul>
                <div className="solution-box">
                  <strong>Solution:</strong> Ensure you don't have any deceptive areas of your site that 
                  could lead to users clicking somewhere they didn't intend. This is especially relevant 
                  with drop-down menus that can cover content areas. If you're all clear there, and you 
                  aren't buying traffic from a shady source, you're getting flagged for what's called IVT, 
                  invalid traffic. In most cases, this is not intentional on the website owner's part and 
                  it's telling you that you have a high percentage of suspicious traffic on your site. 
                  One of the best ways to help resolve this is by installing Cloudflare on your site and 
                  utilizing their Bot Management solution.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Unsupported Language</h3>
                <p>Language Not Supported: Content primarily in a language that AdSense does not support.</p>
                <div className="solution-box">
                  <strong>Solution:</strong> This is a very straightforward rejection. The primary language 
                  of your site isn't one of AdSense's supported languages. You can adjust the primary 
                  language of your website to one AdSense supports. Here's a full list: Arabic, Bengali, 
                  Bulgarian, Chinese (Simplified), Chinese (Traditional), Croatian, Czech, Danish, Dutch, 
                  English, Estonian, Filipino, Finnish, French, German, Greek, Hebrew, Hindi, Hungarian, 
                  Indonesian, Italian, Japanese, Kannada, Korean, Latvian, Lithuanian, Malay, Malayalam, 
                  Marathi, Norwegian, Polish, Portuguese, Romanian, Russian, Serbian, Slovak, Slovenian, 
                  Spanish, Swedish, Tamil, Telugu, Thai, Turkish, Ukrainian, Urdu, Vietnamese.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Privacy Policy Missing</h3>
                <p>Lack of Required Pages: Missing a clear and accessible privacy policy, which is essential for transparency and compliance.</p>
                <div className="solution-box">
                  <strong>Solution:</strong> You need to create and post a privacy policy on your website 
                  that outlines how a user's data is used. You can find templates online that will help 
                  with this. Include a link in the footer of your website to your privacy policy so users 
                  can access it.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Site Behavior and Technical Issues</h3>
                <ul>
                  <li>Automatic Downloads: Initiating downloads without user consent.</li>
                  <li>Malware or Phishing: Hosting malicious software or deceptive content aimed at stealing personal information.</li>
                  <li>Redirects: Automatically redirecting users to unwanted websites or pages.</li>
                </ul>
                <div className="solution-box">
                  <strong>Solution:</strong> These should be obvious, but any type of malicious behavior 
                  is not allowed on your site. This includes automated or misleading redirects, starting 
                  automatic downloads or attempting to steal personal information.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Domain and Ownership Issues</h3>
                <ul>
                  <li>Unverified Ownership: Failure to prove that you own the site or have the necessary permissions.</li>
                  <li>New or Inactive Domain: Domains that are newly registered with little to no content or traffic.</li>
                </ul>
                <div className="solution-box">
                  <strong>Solution:</strong> You either didn't post your ads.txt correctly or didn't place 
                  either of the codes in the correct place on your site. As a reminder, the code you chose 
                  should go between the &lt;head&gt; and &lt;/head&gt; tags on your website on all pages 
                  you wish to monetize.
                </div>
              </div>

              <div className="rejection-section">
                <h3>Rejection: Content Quality Guidelines Not Met</h3>
                <ul>
                  <li>Poor Grammar and Spelling: Content with numerous grammatical errors or typos.</li>
                  <li>Lack of Originality: Content that doesn't provide unique insights or value.</li>
                </ul>
                <div className="solution-box">
                  <strong>Solution:</strong> This is purely related to your content. You either have very 
                  low-quality content or are relying on AI. Either way, you need to rewrite your content 
                  so it provides real value to your audience. Building sites purely to farm low-value 
                  keywords isn't a viable strategy like it was a few years ago.
                </div>
              </div>
            </section>

            <section id="setting-up" className="guide-section">
              <h2>Setting Up Your Approved Site To Serve Ads</h2>
              <p>
                Now that your site is accepted, you're ready to start serving ads on it. I'll walk you 
                through the different types of ad options AdSense has available and the couple of settings 
                you can test to optimize your revenue. However, there's a step we are going to cover first 
                in case you don't have a consent management platform in place. If you do, you can skip 
                this step and move on to Account Information.
              </p>
            </section>

            <section id="privacy-consent" className="guide-section">
              <h2>Privacy and Messaging (Consent Management)</h2>
              <p>
                Consent management is a big deal on today's internet. Users need to be able to determine 
                how you as the site owner can use their data. This includes everything from capturing 
                emails to displaying personalized ads to users. There are a huge number of consent 
                management platforms available to use including InMobi, Truendo, Ketch, and Osana. 
                Google also has built a consent management platform right into AdSense. You can find 
                this under the Privacy and Messaging section in the right-hand menu when you're logged in. 
                From here you can create three types of messages; a US-based consent message for users 
                coming from the US, a consent message for GDPR which encompasses users visiting from a 
                large number of European nations, and an ad block recovery message. The first two are 
                required and the third is optional and generally does not sway many users to help you 
                monetize your site.
              </p>
              <p>
                Follow the steps to create your US and EU-based messages. You'll need the link to your 
                privacy policy and a logo of your website to complete each of these. It will walk you 
                through the steps as you go and is an easy process. Once these are created ensure they 
                are active and if so, congratulations! Your website is now in compliance with privacy 
                laws in a wide array of countries.
              </p>
            </section>

            <section id="account-info" className="guide-section">
              <h2>Account Information</h2>
              <p>
                A quick piece of housekeeping before setting up your ad units is to navigate to Account 
                → Settings → Account Information from the left-hand menu. Once there you'll see a box 
                labeled Seller Information Visibility. You want to enter your domain into the box, switch 
                it to transparent, and hit the verify button. This will switch your account from 
                confidential to private in Google's public sellers list. Advertisers prefer to buy with 
                transparency as it gives them some assurance of where their ads are placed. For publishers, 
                it leads to higher CPMs as some buyers don't allow their ads on confidential websites.
              </p>
            </section>

            <section id="auto-ads" className="guide-section">
              <h2>Deploying Ads: Auto Ads</h2>
              <p>
                Google offers two ways to deploy your ad units, auto ads and standard ads. Auto ads only 
                require a single line of code to be implemented into the &lt;head&gt; area of your website. 
                If you used option b when submitting your site for approval, then the code is already in 
                place. If not, you need to navigate to the Ads section in the left-hand menu and click get 
                code (see screenshot), and paste the code into your site's HTML.
              </p>
              <p>
                Once done, you want to go to the same screen and click the pencil icon on the right side 
                in the same line that has your site listed (see screenshot).
              </p>
              <figure className="guide-image">
                <img 
                  src="/images/adsense-get-code-auto-ads-management.webp" 
                  alt="Google AdSense Ads dashboard showing Get code button for AdSense head code and Manage Auto Ads pencil icon for site-specific ad settings"
                  title="Google AdSense Auto Ads Management - Get Code and Manage Settings"
                  loading="lazy"
                  width="1200"
                  height="800"
                  decoding="async"
                />
                <figcaption>
                  Google AdSense Ads section interface showing the "Get code" button (highlighted with red arrow) 
                  to retrieve your AdSense head code, and the "Manage Auto Ads" pencil icon for configuring 
                  auto ads settings for your specific website.
                </figcaption>
              </figure>
              <p>
                This will take you to the management screen for auto ads. From here you'll see a wide 
                variety of different types of ad units you can deploy on your site automatically. You can 
                turn these on and off almost instantly so you can test out which works the best for your 
                site. When you click each type of ad on the righthand screen the middle screen will show 
                you an example of what it looks like on your site. This process can be slow at times so 
                give it a little bit of time to render what it would look like if it doesn't show up 
                immediately.
              </p>

              <figure className="guide-image">
                <img 
                  src="/images/adsense-auto-ads-settings-preview.webp" 
                  alt="Google AdSense Auto Ads Settings Preview interface showing ad placement configuration with Auto Ad Types including Intent-driven formats, Overlay formats, and In-page formats"
                  title="Google AdSense Auto Ads Settings Preview - Configure Ad Types and Placements"
                  loading="lazy"
                  width="1400"
                  height="900"
                  decoding="async"
                />
                <figcaption>
                  Google AdSense Auto Ads Settings Preview interface showing the ad placement configuration 
                  screen. The sidebar displays Auto Ad Types including Intent-driven formats (Beta), 
                  Overlay formats, and In-page formats, along with options for excluded areas and pages. 
                  Google automatically finds the best ad placements on your site.
                </figcaption>
              </figure>

              <h3>Here's a breakdown of what each ad type does:</h3>

              <h4>Intent-Driven Formats</h4>
              <p>
                This format used to be called anchor texts and was very popular in the late Web 1.0 days. 
                Google will insert small pieces of text into your site at random that are advertising links 
                built around search results and they are a bit intrusive to users. You'll get paid anytime 
                someone clicks one of these links and it has mixed results. For some publishers in very 
                specific niches it can be a solid revenue driver, for more general sites it generally does 
                not produce good results.
              </p>

              <h4>Overlay Formats</h4>
              <p>This section encompasses three different ad types and each is a bit different:</p>
              
              <h5>Anchor Ads</h5>
              <p>
                These ads stick to the top or bottom of your page when a user visits and stays on their 
                screen as they scroll through your site. Some publishers like these since they stay out 
                of the content area and others don't like them. They are usually a good revenue driver, 
                especially for mobile traffic.
              </p>

              <h5>Side Rail Ads</h5>
              <p>
                These ads show up in the left and right white space for desktop users and scroll with them 
                as they move down your site. As with anchor ads, some publishers like these since they 
                stay out of the content area. They only provide revenue for users visiting your site on 
                desktops and laptops.
              </p>

              <h5>Vignette Ads</h5>
              <p>
                The other term for these ads is interstitials. These are full-screen ads that display when 
                a user navigates between pages on your site with a well-defined close button. They serve 
                on both desktop and mobile and in almost all cases are the highest CPM ad AdSense provides. 
                You can set the frequency that they show to users and if you have a sticky site where users 
                go through a good amount of page views each visit they can be a very good source of income. 
                They are intrusive in that the user has to click close to move through them but they're 
                extremely common at this point and most users are used to them.
              </p>

              <h4>In-Page Formats</h4>
              <p>
                Again this section encompasses 3 different types of ad units, but all of these units are 
                automatically inserted into the content of your site. So if you have a blog with articles, 
                ads will be inserted between different paragraphs where Google thinks they will perform best. 
                You can test what the best frequency for showing these ads is by adjusting the ad load 
                slider in this section. Max will show more ads per page while sliding to the left will show 
                fewer ads. Note that serving fewer ads can increase your overall revenue by creating more 
                value for the ads you do show. It also has less impact on your page load and is friendlier 
                to users. Showing the most ads isn't always the best.
              </p>

              <h5>Banner Ads</h5>
              <p>
                These are the most basic form of internet ads. These are the rectangle, square, and 
                horizontal ad banners that you most commonly see across the internet. Google will often use 
                non-standard ad sizes for these so they can be a little bigger than the normal banner sizes.
              </p>

              <h5>Multiplex Ads</h5>
              <p>
                These are Google's version of content recommendation ads. These are blocks that feature a 
                couple of stories from your site mixed in with ads that contain the same look and feel as 
                the rest of the ad block. These can be effective at both generating revenue and driving 
                additional page views for your site. I do recommend not placing them too high up on a page 
                so users don't immediately click off your site if they see something they like.
              </p>

              <h5>Related Search</h5>
              <p>
                This unit is similar to the intent-based ads in that it shows search results related to 
                your content, but it does so in a block with multiple options to click. These can be very 
                effective on highly niche sites since the searches will be very relevant to your topic.
              </p>
            </section>

            <section id="manual-ads" className="guide-section">
              <h2>Deploying Ads: Manual Ads</h2>
              <p>
                Some publishers want to be a little more hands-on with their ads. Worry not, AdSense still 
                has the ability for you to generate ad codes and manually place them. For in-content ads 
                this is generally what I recommend as long as you have an easy way to place them. You can 
                create some consistency around your ad placements and sizes which has a better feel for users. 
                If you're a WordPress site, AdInserter is a great plugin for placing your ads in content 
                areas. Once you've created your ad units, AdSense will give you the code to place on your 
                site and that's it, ads will start serving in those locations after a short time.
              </p>

              <p>There are five different types of manual ad units available from AdSense:</p>

              <h3>Display Ads</h3>
              <p>
                These are the bread and butter standard ads that you see all across the internet. They're 
                the most common form of ad unit and while you can deploy sizeless versions, the most common 
                sizes on desktops are: 970x250, 970x90, 728x90, 300x600, 160x600, 300x250, and 336x280. 
                The most common mobile sizes are 320x50, 320x100 and 300x250. If you have a limited ability 
                to edit your site I'd recommend running sizeless ad units that will simply fit the width of 
                where they are placed no matter if the user is viewing your site on desktop or mobile.
              </p>

              <h3>In-Feed Ads</h3>
              <p>
                These ads are native to your site, meaning they match the look and color scheme. They are 
                good for your homepage and channel pages (If you run a news site, Sports and Politics would 
                be channel pages) between posts or lists. You'll want to test these vs display ads as the 
                performance will depend on your site.
              </p>

              <h3>In-Article Ads</h3>
              <p>
                These ads also match your site but are better shown within your articles or content. Again, 
                you'll want to test these vs display ads and see what performs best.
              </p>

              <h3>Multiplex Ads</h3>
              <p>
                These are content recommendation ads that will feature a few of your articles or content and 
                mix in some native ads in the block. Companies like Taboola and Outbrain offer the same type 
                of ad units but Google's tend to be less spammy looking. These are generally best placed 
                towards the end of articles or in your sidebar space for desktop users.
              </p>

              <h3>Search Engine</h3>
              <p>
                These are limited and not available on all AdSense accounts, but if you do have this option 
                it lets you create a customizable mini search engine for your site. You can let it search 
                the entire web or specify a list of sites it can search. You can generate revenue from these 
                by letting ads appear in the search results, just like they do on Google. These work 
                extremely well for certain verticals and if it's an option in your account I recommend you 
                give it a try.
              </p>
            </section>

            <section id="policies" className="guide-section">
              <h2>Policies and Compliance</h2>
              <p>
                AdSense has strict policies regarding content, ad placement, and invalid activity. Even 
                though your account was approved you still need to keep an eye out for any policy violations 
                that pop up. Usually, these are related to a single page on your site and will not affect 
                the rest of the site, but more severe violations can lead to account suspension or termination. 
                Google will send you an email when you have a policy violation and you can get more 
                information about it by visiting the Policy Center in your AdSense account.
              </p>
            </section>

            <section id="optimization" className="guide-section">
              <h2>Optimization</h2>
              <p>
                The optimization section of your AdSense account is pretty straightforward. Google will 
                recommend optimizations that can be made to your account for increased revenue. You can 
                generally run tests of these suggestions for a period of time and get results before making 
                a final decision. Google does generally push you to include more ad units on the page so be 
                careful about your ad density. As a reminder you want to keep your ad density below 30% 
                meaning ads should not cover more than 30% of the page. Your content needs to stand front 
                and center.
              </p>
            </section>

            <section id="reports" className="guide-section">
              <h2>Reports</h2>
              <p>
                The reports section of your account has a series of built-in report templates that let you 
                slice and dice your data as you wish. You can select which report you'd like to view on the 
                left and what timeframe you'd like to view at the top of the report screen. You can also 
                break down reports by different metrics such as date, platform, or website and filter 
                reports by the same metrics.
              </p>
              <figure className="guide-image">
                <img 
                  src="/images/adsense-reports-interface.webp" 
                  alt="Google AdSense Reports dashboard showing entire account by day report with timeframe selection, breakdown options, metrics tabs, filters, and data table with estimated earnings and performance metrics"
                  title="Google AdSense Reports Interface - View Performance Data and Analytics"
                  loading="lazy"
                  width="1400"
                  height="900"
                  decoding="async"
                />
                <figcaption>
                  Google AdSense Reports interface displaying the "Entire account by day" report. The interface 
                  shows timeframe selection options (Today, Last 7 days, Last 30 days, etc.), breakdown options 
                  for segmenting data by different metrics, multiple metric tabs (Estimated earnings, Page views, 
                  Page RPM, Impressions, etc.), search and filter capabilities, and a detailed data table showing 
                  performance metrics including estimated earnings, page views, impressions, and clicks.
                </figcaption>
              </figure>
            </section>

            <section className="guide-section conclusion">
              <h2>Conclusion</h2>
              <p>
                As you can see, AdSense is a very straightforward and easy-to-use platform. When starting 
                out monetizing your website, it's the perfect place to start. However, once you grow to a 
                few million impressions, you'll want to explore more advanced options that allow you to bring 
                in more ad exchanges to compete with Google, let you deploy more advanced ad unit setups, 
                traffic direct ad campaigns, provide more in-depth reporting and help you generate a higher 
                RPM for your site. In our next guide we will cover Google Ad Manager, Google's Ad Server. 
                I'll help you understand what it can do, how to set it up, and the benefits of getting a 
                Google AdX (the bigger version of AdSense) account for publishers.
              </p>
            </section>
          </main>
        </div>
      </div>
    </div>
  );
};

export default AdSenseGuide;

